import {
  CalendarBlankIcon,
  ChatDotsIcon,
  ClockClockwiseIcon,
  ClockIcon,
  CoinsIcon,
  GiftIcon,
  LightningSlashIcon,
  MoneyIcon,
  ProhibitIcon,
  TagIcon,
} from '@phosphor-icons/react'

import { ChatAlertIcon, GiftCardIcon, OriginalPaymentMethodIcon } from '@reveni-io/reveni-ui'

import { initCriterion } from '@/common/components/ConditionsEditor/constants'
import { refundHoldModeValues } from '@/common/constants'

import { bonusAppliesToValues } from './ActionConfigs/ApplyBonusConfig/constants'
import { feeApplicationModeValues } from './ActionConfigs/ApplyExtraFeeConfig/constants'
import { triggerModeValues } from './ActionConfigs/constants'
import { emptyCustomMessage } from './CustomMessage/constants'

export const actionTypeValues = {
  blockOrderOptions: 'block_order_options',
  blockItemOptions: 'block_item_options',
  disableInstant: 'disable_instant',
  extendWindow: 'extend_window',
  putOnHold: 'put_on_hold',
  putRefundOnHold: 'put_refund_on_hold',
  applyBonus: 'apply_bonus',
  applyExtraFee: 'apply_extra_fee',
  forceRefundTrigger: 'force_refund_trigger',
  forceExchangeOrderTrigger: 'force_exchange_order_trigger',
  forceRefundMethod: 'force_refund_method',
  disableRefundMethods: 'disable_refund_methods',
  addTags: 'add_tags',
  displayMessage: 'display_message',
  displayConsentModal: 'display_consent_modal',
}

export const backendActionTypes = {
  forceDisableRefundMethods: 'force_disable_refund_methods',
  applyFeeItem: 'apply_fee_item',
  applyFeeRequest: 'apply_fee_request',
}

export const actionTypes = {
  [actionTypeValues.blockOrderOptions]: {
    label: 'Bloquear opción para el pedido',
    icon: ProhibitIcon,
    description:
      'Bloquear una opción impide que los clientes elijan ese tipo de solicitud para cualquier producto del pedido. Si se bloquean todas las opciones, el pedido completo dejará de ser elegible para devolución y los clientes verán el cuadro de diálogo de error correspondiente al intentar iniciar el proceso.',
    defaultConfig: {
      block_returns: false,
      block_variant_exchanges: false,
      block_exchange_for_other: false,
      custom_message_enabled: false,
      custom_message: emptyCustomMessage,
      custom_message_type: null,
    },
  },
  [actionTypeValues.blockItemOptions]: {
    label: 'Bloquear opción para producto',
    icon: ProhibitIcon,
    description:
      'Bloquear una opción impide que los clientes elijan ese tipo de solicitud para aquellos productos de pedido que cumplan la condición seleccionada. Si se bloquean todas las opciones, el producto se mostrará como "Bloqueado" y no será posible seleccionarlo.',
    defaultConfig: {
      block_returns: false,
      block_variant_exchanges: false,
      block_exchange_for_other: false,
      product_criteria: [initCriterion],
      custom_message_enabled: false,
      custom_message: emptyCustomMessage,
      custom_message_type: null,
    },
  },
  [actionTypeValues.disableInstant]: {
    label: 'Deshabilitar opciones instantáneas',
    icon: LightningSlashIcon,
    description:
      'Deshabilitar las opciones instantáneas impide que los clientes reciban una resolución instantánea para los tipos de transacción seleccionados, aunque sean elegibles.',
    defaultConfig: { disable_returns: true, disable_exchanges: true },
  },
  [actionTypeValues.extendWindow]: {
    label: 'Ampliar ventana de devoluciones/cambios',
    icon: CalendarBlankIcon,
    description:
      'Amplía el plazo para devoluciones y cambios definido en la política predeterminada hasta una fecha específica o por un número determinado de días adicionales.',
    defaultConfig: {
      extend_returns: true,
      extend_exchanges: false,
      extend_until_date: null,
      extend_by_days: null,
    },
  },
  [actionTypeValues.putOnHold]: {
    label: 'Poner solicitud En Espera',
    icon: ClockIcon,
    description:
      'Poner las solicitudes en espera para revisión manual antes de generar una etiqueta de devolución y enviarla al cliente.',
    defaultConfig: { hold_reason: '' },
  },
  [actionTypeValues.putRefundOnHold]: {
    label: 'Retener reembolso',
    icon: MoneyIcon,
    description: 'Retiene los reembolsos automáticos para revisión manual.',
    defaultConfig: {
      hold_reason: '',
      refund_hold_mode: refundHoldModeValues.holdIndefinitely,
      refund_hold_delay_days: null,
    },
  },
  [actionTypeValues.applyBonus]: {
    label: 'Aplicar bonificación',
    icon: GiftIcon,
    description:
      'Aplica beneficios especiales a las solicitudes que cumplan los requisitos, como la exención de los gastos de devolución o el reembolso de los gastos de envío originales.',
    defaultConfig: {
      bonus_free_return: false,
      bonus_free_exchange: false,
      bonus_refund_shipping: false,
      bonus_exchange_discount: false,
      bonus_exchange_discount_type: '',
      bonus_exchange_discount_value: '',
      bonus_exchange_discount_currency: null,
      bonus_exchange_discount_label: '',
      bonus_exchange_discount_applies_to: bonusAppliesToValues.exchangeOrder,
      product_criteria: [initCriterion],
    },
  },
  [actionTypeValues.applyExtraFee]: {
    label: 'Aplicar tasa adicional',
    icon: CoinsIcon,
    description:
      'Aplica tasas adicionales a las solicitudes que cumplan los requisitos. Estas aparecerán como partidas separadas en el desglose final del resultado.',
    defaultConfig: {
      fee_concept: '',
      fee_amount: '',
      fee_currency: '',
      fee_application_mode: feeApplicationModeValues.perRequest,
      product_criteria: [initCriterion],
      custom_message_enabled: false,
      custom_message: emptyCustomMessage,
      custom_message_type: null,
    },
  },
  [actionTypeValues.forceRefundTrigger]: {
    label: 'Forzar trigger de reembolso',
    icon: ClockClockwiseIcon,
    description:
      'Sobrescribe el trigger de reembolso global definido en Configuración > Reembolsos para las solicitudes que cumplan las condiciones de la regla.',
    defaultConfig: {
      refund_trigger_status: '',
      refund_trigger_mode: triggerModeValues.atTrigger,
      refund_trigger_delay_days: null,
      refund_trigger_delay_reason: '',
    },
  },
  [actionTypeValues.forceExchangeOrderTrigger]: {
    label: 'Forzar trigger de creación de exchange',
    icon: ClockClockwiseIcon,
    description:
      'Sobrescribe el trigger global de creación del pedido de cambio definido en Configuración > Política de devoluciones para las solicitudes que cumplan las condiciones de la regla.',
    defaultConfig: {
      exchange_trigger_status: '',
      exchange_trigger_mode: triggerModeValues.atTrigger,
      exchange_trigger_delay_days: null,
      exchange_trigger_delay_reason: '',
    },
  },
  [actionTypeValues.forceRefundMethod]: {
    label: 'Forzar método de reembolso',
    icon: GiftCardIcon,
    description:
      'Forzar un método de reembolso específico para las solicitudes afectadas. Esto ignorará la configuración predeterminada haciendo que el método seleccionado sea la única opción disponible para el cliente.',
    defaultConfig: { refund_method: '', ignore_extra: true },
  },
  [actionTypeValues.disableRefundMethods]: {
    label: 'Deshabilitar métodos de reembolso',
    icon: OriginalPaymentMethodIcon,
    description:
      'Desactivar métodos de reembolso específicos para las solicitudes afectadas, ignorando la configuración predeterminada.',
    defaultConfig: { refund_methods: [] },
  },
  [actionTypeValues.addTags]: {
    label: 'Etiquetar solicitud',
    icon: TagIcon,
    description:
      'Añade etiquetas a la solicitud para poder segmentarla, filtrarla o utilizarla en automatizaciones más adelante.',
    defaultConfig: { tags: [] },
  },
  [actionTypeValues.displayMessage]: {
    label: 'Mostrar mensaje',
    icon: ChatDotsIcon,
    description: 'Muestra un mensaje al cliente.',
    defaultConfig: {
      custom_message: emptyCustomMessage,
      custom_message_type: null,
      display_message_placement: '',
      product_criteria: [initCriterion],
    },
  },
  [actionTypeValues.displayConsentModal]: {
    label: 'Mostrar modal de consentimiento',
    icon: ChatAlertIcon,
    description: 'Muestra un modal de confirmación que bloquea el paso al siguiente paso hasta que el customer acepta.',
    defaultConfig: {
      consent_title: emptyCustomMessage,
      consent_description: emptyCustomMessage,
      consent_checkbox_text: emptyCustomMessage,
      display_message_placement: '',
    },
  },
}
